import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Icon, Spinner, StatusBadge, CardTitle } from '../components/ui';

type Page = 'dashboard' | 'generate' | 'library' | 'settings' | 'billing';

interface DashboardProps {
  onNavigate: (page: Page) => void;
}

interface UsageData {
  tier_name: string;
  period: { start: string; end: string };
  characters: {
    used: number;
    limit: number;
    remaining: number;
    percentage_used: number;
  };
  audio_files_generated: number;
  daily?: Array<{
    date: string;
    characters_used: number;
    audio_files_generated: number;
  }>;
}

interface RecentJob {
  job_id: string;
  status: string;
  character_count: number;
  voice_name: string | null;
  queued_at: string;
  audio_url: string | null;
}

interface WeekBar {
  key: string;
  letter: string;
  pct: number;
  isMax: boolean;
}

function buildWeekly(daily: UsageData['daily']): WeekBar[] {
  const byDate = new Map<string, number>();
  for (const d of daily || []) {
    byDate.set(d.date.slice(0, 10), d.characters_used);
  }
  const days: Array<{ key: string; letter: string; value: number }> = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;
    days.push({
      key,
      letter: d.toLocaleDateString('en-US', { weekday: 'narrow' }),
      value: byDate.get(key) ?? 0,
    });
  }
  const max = Math.max(...days.map((d) => d.value));
  return days.map((d) => ({
    key: d.key,
    letter: d.letter,
    pct: max > 0 ? Math.max(6, Math.round((d.value / max) * 100)) : 6,
    isMax: max > 0 && d.value === max,
  }));
}

export function Dashboard({ onNavigate }: DashboardProps) {
  const { user } = useAuth();
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [recentJobs, setRecentJobs] = useState<RecentJob[]>([]);
  const [fileCount, setFileCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [usageResult, jobsResult, filesResult] = await Promise.all([
        api.getUsage(),
        api.getJobs({ limit: 4 }),
        api.getAudioFiles({ limit: 100 }),
      ]);

      if (usageResult.success && usageResult.data) {
        setUsage(usageResult.data);
      }

      if (jobsResult.success && jobsResult.data) {
        setRecentJobs(jobsResult.data);
      }

      if (filesResult.success && filesResult.data) {
        setFileCount(filesResult.data.length);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    }
    setLoading(false);
  };

  const formatNumber = (num: number) => num.toLocaleString();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={36} />
      </div>
    );
  }

  const pct = usage ? Math.min(usage.characters.percentage_used, 100) : 0;
  const weekly = buildWeekly(usage?.daily);

  return (
    <div className="fx flex flex-col gap-[22px] max-w-[1120px]">
      {/* Welcome */}
      <div>
        <h2 className="font-display font-extrabold text-white text-[26px] m-0 mb-1">
          Welcome back, {user?.first_name || 'there'}
        </h2>
        <p className="text-mist-400 text-[14.5px] m-0">Here's your audio activity this month.</p>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={() => onNavigate('generate')}
          className="lift flex items-center gap-3.5 p-5 rounded-[14px] text-left cursor-pointer"
          style={{
            border: '1px solid rgba(var(--a-rgb),.32)',
            background: 'linear-gradient(135deg,rgba(var(--a-rgb),.16),#17171B 70%)',
          }}
        >
          <span className="grad-accent-simple w-[46px] h-[46px] rounded-xl flex-none flex items-center justify-center text-ink-950">
            <Icon name="generate" size={22} />
          </span>
          <span>
            <span className="block text-white font-bold text-[15px]">Generate Audio</span>
            <span className="block text-mist-500 text-[13px] mt-0.5">Convert text to speech</span>
          </span>
        </button>

        <button
          onClick={() => onNavigate('library')}
          className="lift flex items-center gap-3.5 p-5 rounded-[14px] border border-ink-700 text-left cursor-pointer"
          style={{ background: 'linear-gradient(135deg,rgba(31,143,224,.13),#17171B 70%)' }}
        >
          <span
            className="w-[46px] h-[46px] rounded-xl flex-none flex items-center justify-center"
            style={{
              color: 'var(--a-hi)',
              background: 'rgba(var(--a-rgb),.12)',
              border: '1px solid rgba(var(--a-rgb),.3)',
            }}
          >
            <Icon name="library" size={22} />
          </span>
          <span>
            <span className="block text-white font-bold text-[15px]">Audio Library</span>
            <span className="block text-mist-500 text-[13px] mt-0.5">
              {fileCount !== null
                ? `${formatNumber(fileCount)} ${fileCount === 1 ? 'file' : 'files'} ready`
                : 'View generated files'}
            </span>
          </span>
        </button>

        <button
          onClick={() => onNavigate('billing')}
          className="lift flex items-center gap-3.5 p-5 rounded-[14px] border border-ink-700 text-left cursor-pointer"
          style={{ background: 'linear-gradient(135deg,rgba(46,139,87,.13),#17171B 70%)' }}
        >
          <span
            className="w-[46px] h-[46px] rounded-xl flex-none flex items-center justify-center"
            style={{
              color: 'var(--a-hi)',
              background: 'rgba(var(--a-rgb),.12)',
              border: '1px solid rgba(var(--a-rgb),.3)',
            }}
          >
            <Icon name="billing" size={22} />
          </span>
          <span>
            <span className="block text-white font-bold text-[15px]">Upgrade Plan</span>
            <span className="block text-mist-500 text-[13px] mt-0.5">
              {usage?.tier_name ? `On ${usage.tier_name} · Get more characters` : 'Get more characters'}
            </span>
          </span>
        </button>
      </div>

      {/* Usage stats */}
      {usage && (
        <div className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr] gap-4">
          {/* Characters used */}
          <div
            className="p-6 rounded-2xl"
            style={{
              border: '1px solid rgba(var(--a-rgb),.32)',
              background:
                'linear-gradient(150deg,rgba(var(--a-rgb),.16) 0%,rgba(23,23,27,.4) 46%,#17171B 100%)',
            }}
          >
            <div className="flex justify-between items-end mb-[18px]">
              <div>
                <p className="m-0 text-mist-500 text-[12.5px] font-semibold uppercase tracking-[0.14em]">
                  Characters used
                </p>
                <p className="mt-1.5 m-0 font-display font-extrabold text-white text-[34px] leading-tight">
                  {formatNumber(usage.characters.used)}
                  <span className="text-mist-500 text-[17px] font-semibold">
                    {' '}
                    / {formatNumber(usage.characters.limit)}
                  </span>
                </p>
              </div>
              <span className="font-bold text-sm" style={{ color: 'var(--a-hi)' }}>
                {usage.characters.percentage_used}%
              </span>
            </div>
            <div className="h-[9px] rounded-full bg-ink-700 overflow-hidden mb-[26px]">
              <div className="grad-bar h-full rounded-full" style={{ width: `${pct}%` }} />
            </div>
            {/* Weekly mini bar chart */}
            <div className="flex items-end gap-[9px] h-[90px]">
              {weekly.map((b) => (
                <div key={b.key} className="flex-1 h-full flex flex-col items-center justify-end gap-2">
                  <div
                    className="w-full rounded-[5px]"
                    style={{
                      height: `${b.pct}%`,
                      background: b.isMax
                        ? 'linear-gradient(180deg,var(--a-hi),var(--a-lo))'
                        : '#2C2C33',
                    }}
                  />
                  <span className="text-mist-500 text-[11px]">{b.letter}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stat cards */}
          <div className="flex flex-col gap-4">
            <div
              className="p-[22px] rounded-2xl border border-ink-700 flex-1"
              style={{ background: 'linear-gradient(155deg,rgba(31,143,224,.12) 0%,#17171B 60%)' }}
            >
              <p className="m-0 text-mist-500 text-[12.5px] font-semibold uppercase tracking-[0.14em]">
                Files generated
              </p>
              <p className="mt-2 m-0 font-display font-extrabold text-white text-[32px]">
                {formatNumber(usage.audio_files_generated)}
              </p>
            </div>
            <div
              className="p-[22px] rounded-2xl border border-ink-700 flex-1"
              style={{ background: 'linear-gradient(155deg,rgba(46,139,87,.13) 0%,#17171B 60%)' }}
            >
              <p className="m-0 text-mist-500 text-[12.5px] font-semibold uppercase tracking-[0.14em]">
                Characters left
              </p>
              <p className="mt-2 m-0 font-display font-extrabold text-white text-[32px]">
                {formatNumber(usage.characters.remaining)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Recent activity */}
      <div className="card">
        <div className="flex justify-between items-center mb-3.5">
          <CardTitle>Recent activity</CardTitle>
          <button
            onClick={() => onNavigate('library')}
            className="bg-transparent border-0 p-0 text-[13px] font-semibold cursor-pointer"
            style={{ color: 'var(--a-link)' }}
          >
            View all
          </button>
        </div>

        {recentJobs.length === 0 ? (
          <div className="text-center py-8">
            <span className="inline-flex text-mist-500">
              <Icon name="generate" size={44} />
            </span>
            <h3 className="mt-3 text-sm font-semibold text-mist-200">No audio generated yet</h3>
            <p className="mt-1 text-sm text-mist-400">Get started by generating your first audio.</p>
            <button onClick={() => onNavigate('generate')} className="mt-5 btn-grad">
              Generate Audio
            </button>
          </div>
        ) : (
          <div className="flex flex-col">
            {recentJobs.map((job) => (
              <div key={job.job_id} className="border-t border-ink-800 py-[13px]">
                {/* Mobile row */}
                <div className="flex items-center justify-between gap-3 md:hidden">
                  <div className="min-w-0">
                    <p className="m-0 text-mist-200 text-[13.5px] font-semibold truncate">
                      {job.voice_name || 'Unknown voice'}
                    </p>
                    <p className="m-0 mt-0.5 text-mist-500 text-[11.5px]">
                      {formatNumber(job.character_count)} ch · {formatDate(job.queued_at)}
                    </p>
                  </div>
                  <StatusBadge status={job.status} />
                </div>
                {/* Desktop row */}
                <div className="hidden md:grid grid-cols-[130px_1fr_100px_160px_auto] items-center gap-3">
                  <div>
                    <StatusBadge status={job.status} />
                  </div>
                  <span className="text-mist-200 text-sm font-medium truncate">
                    {job.voice_name || 'Unknown voice'}
                  </span>
                  <span className="text-mist-400 text-[13.5px]">
                    {formatNumber(job.character_count)} ch
                  </span>
                  <span className="text-mist-500 text-[13px]">{formatDate(job.queued_at)}</span>
                  <span className="text-right">
                    {job.audio_url && (
                      <a
                        href={job.audio_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[13px] font-semibold no-underline"
                        style={{ color: 'var(--a-link)' }}
                      >
                        Download
                      </a>
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
